import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, BadgeCheck } from "lucide-react";
import { getJson } from "../lib/http";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROTATE_MS = 7000;

// Module cache so the desktop (hero) and mobile instances share one fetch.
let _cache = null;

function useRotatingReviews() {
  const [reviews, setReviews] = useState(_cache || []);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    if (_cache) return;
    let active = true;
    getJson(`${API}/reviews`)
      .then((data) => {
        const best = (data?.reviews || [])
          .filter((r) => r.verified && r.comment && r.rating >= 4)
          .slice(0, 12);
        _cache = best;
        if (active) setReviews(best);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (reviews.length < 2) return;
    timer.current = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % reviews.length);
        setVisible(true);
      }, 400);
    }, ROTATE_MS);
    return () => clearInterval(timer.current);
  }, [reviews.length]);

  return { reviews, idx, visible };
}

function Stars({ rating, size }) {
  return (
    <span className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={n <= rating ? "fill-[#ffc531] text-[#ffc531]" : "text-white/25"}
        />
      ))}
    </span>
  );
}

function VerifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#7ee2a8]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#7ee2a8]">
      <BadgeCheck size={11} /> Verified buyer
    </span>
  );
}

function ProductPill({ review }) {
  return (
    <Link
      to={`/shop/${review.product_id}`}
      data-testid="review-highlight-product-pill"
      className="rounded-full border border-white/20 px-2.5 py-0.5 text-[11px] text-[#7bc9ea] hover:bg-white/5"
    >
      {review.product_name}
    </Link>
  );
}

function Dots({ reviews, idx }) {
  if (reviews.length < 2) return null;
  return (
    <span className="inline-flex items-center gap-1.5" aria-hidden="true">
      {reviews.map((_, i) => (
        <span
          key={i}
          className={
            "h-1.5 rounded-full transition-all duration-300 " +
            (i === idx ? "w-5 bg-[#20a6da]" : "w-1.5 bg-white/20")
          }
        />
      ))}
    </span>
  );
}

// inline: compact left-aligned card used inside the desktop hero column.
// Default: full-width centered section (used on mobile home).
export default function ReviewHighlights({ inline = false }) {
  const { reviews, idx, visible } = useRotatingReviews();
  if (!reviews.length) return null;
  const r = reviews[idx];

  if (inline) {
    return (
      <div
        data-testid="review-highlights"
        className="mt-8 max-w-xl rounded-md border border-white/10 bg-white/[0.03] p-5 animate-slide-up"
      >
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8fa3c0]">
            What verified buyers say
          </p>
          <span className="ml-auto">
            <Dots reviews={reviews} idx={idx} />
          </span>
        </div>
        <div
          data-testid="review-highlight-quote"
          style={{ opacity: visible ? 1 : 0, transition: "opacity 0.4s ease" }}
        >
          <div className="mt-3 flex items-center gap-2">
            <Stars rating={r.rating} size={14} />
          </div>
          <blockquote className="mt-2 text-base leading-snug text-white">
            “{r.comment}”
          </blockquote>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#bfcde3]">
            <span className="font-medium text-white">{r.name}</span>
            {r.city ? <span className="text-[#8fa3c0]">· {r.city}</span> : null}
            <VerifiedBadge />
            <ProductPill review={r} />
            <Link
              to="/reviews"
              data-testid="review-highlights-all-link"
              className="ml-auto text-[11px] font-semibold uppercase tracking-[0.15em] text-[#20a6da] hover:text-[#7bc9ea] transition-colors"
            >
              All reviews →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid="review-highlights"
      className="border-b border-white/5 bg-[#0a0a0a]"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-12 sm:py-16 text-center">
        <p className="section-tag justify-center">What verified buyers say</p>
        <div
          data-testid="review-highlight-quote"
          style={{ opacity: visible ? 1 : 0, transition: "opacity 0.4s ease" }}
        >
          <div className="mt-6">
            <Stars rating={r.rating} size={16} />
          </div>
          <blockquote className="mt-4 font-display text-xl sm:text-2xl leading-snug text-white">
            “{r.comment}”
          </blockquote>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm text-[#bfcde3]">
            <span className="font-medium text-white">{r.name}</span>
            {r.city ? <span className="text-[#8fa3c0]">· {r.city}</span> : null}
            <VerifiedBadge />
            <ProductPill review={r} />
          </div>
        </div>
        <div className="mt-6 flex items-center justify-center">
          <Dots reviews={reviews} idx={idx} />
        </div>
        <Link
          to="/reviews"
          data-testid="review-highlights-all-link"
          className="mt-6 inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#20a6da] hover:text-[#7bc9ea] transition-colors"
        >
          Read all reviews →
        </Link>
      </div>
    </section>
  );
}
